import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Calendar,
  MapPin,
  CheckCircle,
  Heart,
  ArrowLeft,
  Users,
  Briefcase,
  GraduationCap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import { cn } from '@/lib/utils';
import LeergangOnderstroomDialog from '@/components/LeergangOnderstroomDialog';
import voorwaardenPdf from '@/assets/Algemene-voorwaarden-Systemique.pdf';
import OptimizedImage from '@/components/OptimizedImage';
import leergangSession from '@/assets/systemique/leergang-session.webp';
import leergangOpstelling from '@/assets/systemique/leergang-opstelling.webp';
import { SEO } from '@/components/SEO';
import {
  LEERGANG_SEO_TITLE,
  LEERGANG_SHORT_LABEL,
  SITE_NAME,
  SITE_ORIGIN,
} from '@/seo/siteConfig';

const SpotlightCard = ({ children, className = '', ...props }: React.ComponentProps<'div'>) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) setGradientSize(300);
      else if (width < 1024) setGradientSize(450);
      else setGradientSize(600);
    };
    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={() => setOpacity(1)}
      onBlur={() => setOpacity(0)}
      onMouseEnter={() => setOpacity(1)}
      onMouseLeave={() => setOpacity(0)}
      className={cn(
        'relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full',
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

interface LeergangEditie {
  id: string;
  seizoen: string;
  jaar: number;
  startDate: string;
  endDate: string;
  moduleDates: string[];
  location: {
    name: string;
    url: string;
    address: {
      streetAddress: string;
      addressLocality: string;
      postalCode: string;
    };
  };
}

const featuredEditie: LeergangEditie = {
  id: 'najaar-2026',
  seizoen: 'Najaar',
  jaar: 2026,
  startDate: '2026-10-29',
  endDate: '2026-11-27',
  moduleDates: ['29 en 30 oktober 2026', '27 november 2026'],
  location: {
    name: 'Stadsoase naar Zee',
    url: 'https://www.stadsoasenaarzee.com',
    address: {
      streetAddress: 'Bert Haanstrakade 102',
      addressLocality: 'Amsterdam',
      postalCode: '1087 DN',
    },
  },
};

const leerdoelen = [
  'Belangrijkste systeemdynamieken herkennen',
  'Je eigen systemische patronen doorgronden',
  'Praktisch handelingsperspectief voor casuïstiek',
  'Eigen organisatieopstelling met vaak diepe inzichten',
];

const voorWieCards = [
  {
    icon: Briefcase,
    title: 'Leidinggevenden',
    text: 'Je voelt dat er meer speelt dan wat in vergaderingen wordt gezegd, en wilt de onderstroom benoemen.',
  },
  {
    icon: Users,
    title: 'HR & adviseurs',
    text: 'Je begeleidt verandering en wilt scherper zien waar het systeem vastzit en wat beweging vraagt.',
  },
  {
    icon: GraduationCap,
    title: 'Ervaren professionals',
    text: 'Je kent opstellingen of systemisch werk en wilt het verankeren in je dagelijkse leiderschapspraktijk.',
  },
];

const programmaModules = [
  {
    title: 'Module 1 (2 dagen)',
    subtitle: 'Duiken in de onderstroom',
    description:
      'Tijdens deze module ligt de focus op het doorgronden van de belangrijkste systemische wetmatigheden, dynamieken en patronen. Dit doen we aan de hand van theorie en de werkpraktijk van de deelnemers.',
  },
  {
    title: 'Module 2 (1 dag)',
    subtitle: 'Waarnemen en interveniëren',
    description:
      'Alle deelnemers werken aan een real life case. Je leert systemisch waar te nemen in gesprekken en interventies te doen. We werken met verschillende real life cases en onderzoeken welke interventies effect hebben.',
  },
];

const uspItems = [
  {
    title: 'Voor leiders & adviseurs',
    text: 'Speciaal voor leiders en adviseurs die systemische bril willen toevoegen.',
  },
  {
    title: 'Eigen organisatieopstelling',
    text: 'Je krijgt je eigen organisatieopstelling met vaak impactvolle inzichten.',
  },
  {
    title: 'Compact maar krachtig',
    text: 'De opleiding is compact, maar rijk aan stof en inzichten.',
  },
  {
    title: 'Kleine groep',
    text: 'Je leert in een kleine groep (max 8 deelnemers) voor meer diepgang.',
  },
];

const leergangReviews = [
  {
    id: 'review-1',
    rating: 9,
    body: 'De leergang heeft mij meer bewustzijn, taal en lef gegeven om te kijken naar en te werken met de onderstroom. Ik ben mij bewuster van mijn eigen rol, zie scherper wat er speelt binnen de organisatie en durf dit ook vaker bespreekbaar te maken.',
  },
  {
    id: 'review-2',
    rating: 9,
    body: 'De bewustwording en bril van systemisch kijken heeft veel in beweging gezet. Op persoonlijk vlak over mijn eigen positie. Vooral welk patroon ik nu doorbreek en wat ik nodig heb (duidelijke plek).',
  },
  {
    id: 'review-3',
    rating: 9,
    body: 'Enorm veel inzichten op systemisch vlak voor mijn team en persoonlijk.',
  },
];

const leergangFaq = [
  {
    id: 'opleiding-of-leergang',
    question: 'Is dit een opleiding of een leergang systemisch leiderschap?',
    answer:
      'Dit is een leergang systemisch leiderschap: een compact traject van 3 dagen, geen meerjarige opleiding. Je leert pragmatisch systemisch kijken en handelen, direct toepasbaar in je leiderschaps- of adviseursrol.',
  },
  {
    id: 'verschil-systemisch-werk-leiderschap',
    question: 'Wat is het verschil tussen systemisch werk en systemisch leiderschap?',
    answer:
      'Systemisch werk is de methodiek: kijken naar patronen, loyaliteiten en de onderstroom in systemen. Systemisch leiderschap is de toepassing daarvan in je rol als leider of adviseur: herkennen wat er speelt en daar concreet mee werken in gesprekken, besluiten en interventies.',
  },
  {
    id: 'waar-vindt-leergang-plaats',
    question: 'Waar vindt Duiken in de Onderstroom plaats?',
    answer:
      'De leergang vindt plaats bij Stadsoase naar Zee in Amsterdam (Bert Haanstrakade 102). Marthe van der Molen werkt vanuit Castricum en bedient de regio Noord-Holland en Amsterdam.',
  },
  {
    id: 'voor-wie-bedoeld',
    question: 'Voor wie is deze leergang systemisch leiderschap bedoeld?',
    answer:
      'Voor leiders, managers, adviseurs en HR-professionals die systemisch willen leiden, niet alleen analyseren. Je hebt ervaring in een leidinggevende of adviserende rol en wilt de systemische bril toevoegen aan je praktijk.',
  },
  {
    id: 'wanneer-waar',
    question: 'Wanneer en waar vindt de leergang plaats?',
    answer: `De volgende editie is ${featuredEditie.seizoen} ${featuredEditie.jaar}: ${featuredEditie.moduleDates.join(' · ')}. Locatie: ${featuredEditie.location.name}, Amsterdam.`,
  },
  {
    id: 'wie-begeleidt',
    question: 'Wie begeleidt de leergang?',
    answer:
      'Marthe van der Molen, systemisch organisatiecoach en trainer. Ze begeleidt leiders, adviseurs, teams en organisaties op het snijvlak van praktische verandervraagstukken en systemische onderstromen. Haar stijl is helder, warm en confronterend als het nodig is.',
  },
  {
    id: 'intake-eindcoaching',
    question: 'Hoe werkt intake en eindcoaching?',
    answer:
      'Bij aanmelding vindt een intake plaats waarin we jouw ontwikkelbehoefte verkennen. Twee maanden na afloop heb je een coachgesprek om te reflecteren op de inzichten en waar nodig een verdiepingsslag te maken.',
  },
  {
    id: 'kosten',
    question: 'Wat kost de leergang?',
    answer:
      'De investering is € 1.500 excl. btw. Dit omvat de 3-daagse leergang, intake vóór start en eindcoaching na afloop. Er is plek voor maximaal 8 deelnemers. Vol = vol.',
  },
] as const;

const heroStats = ['3 dagen', 'Max. 8 deelnemers', 'Najaar 2026', '€ 1.500 excl. btw'];

const generateCourseSchema = (editie: LeergangEditie) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  courseName: `${LEERGANG_SHORT_LABEL} | Duiken in de Onderstroom (${editie.seizoen} ${editie.jaar})`,
  description:
    '3-daagse leergang systemisch leiderschap voor leiders en adviseurs. Leer pragmatisch systemisch kijken, handelen en de onderstroom herkennen.',
  provider: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Willem de Zwijgerlaan 14',
      addressLocality: 'Castricum',
      postalCode: '1901 CR',
      addressRegion: 'Noord-Holland',
      addressCountry: 'NL',
    },
    telephone: '+31-6-12991453',
    email: 'marthe@systemique.nl',
  },
  courseCode: `LEERGANG-ONDERSTROOM-${editie.seizoen.toUpperCase()}-${editie.jaar}`,
  educationalLevel: 'Professional',
  teaches: [
    'Systemisch leiderschap',
    'Systemisch werk',
    'Organisatieopstellingen',
    'Systemische interventies',
    'Onderstroom in organisaties',
  ],
  timeRequired: 'P3D',
  inLanguage: 'nl',
  audience: {
    '@type': 'Audience',
    audienceType: 'Leiders, managers, adviseurs en HR-professionals',
  },
  coursePrerequisites: 'Ervaring in leiderschap of advisering',
  offers: {
    '@type': 'Offer',
    price: '1500',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/InStock',
    url: `${SITE_ORIGIN}/leergang-onderstroom`,
  },
  url: `${SITE_ORIGIN}/leergang-onderstroom`,
  startDate: editie.startDate,
  endDate: editie.endDate,
  eventStatus: 'https://schema.org/EventScheduled',
  location: {
    '@type': 'Place',
    name: editie.location.name,
    url: editie.location.url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: editie.location.address.streetAddress,
      addressLocality: editie.location.address.addressLocality,
      postalCode: editie.location.address.postalCode,
      addressRegion: 'Noord-Holland',
      addressCountry: 'NL',
    },
  },
});

const LeergangOnderstroom = () => {
  const [leergangDialogOpen, setLeergangDialogOpen] = useState(false);

  const startDatumLabel = new Date(featuredEditie.startDate).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      <SEO
        title={LEERGANG_SEO_TITLE}
        description="Leergang systemisch leiderschap: Duiken in de Onderstroom. Compacte 3-daagse leergang voor leiders en adviseurs in Amsterdam. Najaar 2026, max. 8 deelnemers. € 1.500 excl. btw."
        keywords="leergang systemisch leiderschap, systemische leergang voor leiders, Duiken in de Onderstroom, leergang systemisch werk Amsterdam, opleiding systemisch leiderschap, leergang onderstroom, systemische opleiding Amsterdam, leergang najaar 2026"
        canonical={`${SITE_ORIGIN}/leergang-onderstroom`}
        ogTitle={LEERGANG_SEO_TITLE}
        ogDescription="3-daagse leergang systemisch leiderschap voor leiders en adviseurs. Duiken in de Onderstroom, najaar 2026 in Amsterdam."
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateCourseSchema(featuredEditie)),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: leergangFaq.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="relative min-h-[55vh] md:min-h-[65vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30" />
        </div>

        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <Button
            variant="ghost"
            asChild
            className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer"
          >
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>

          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#F9EBEF]/80 mb-6">
            {SITE_NAME} · Amsterdam &amp; Noord-Holland
          </p>

          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl text-[#F9EBEF] mb-4">
            {LEERGANG_SHORT_LABEL}
          </h1>

          <p className="text-2xl md:text-3xl text-[#F9EBEF]/90 font-serif italic mb-6">
            Duiken in de Onderstroom
          </p>

          <p className="text-base md:text-lg text-[#F9EBEF]/80 max-w-2xl mx-auto mb-8 font-light leading-relaxed">
            Compacte leergang (3 dagen) voor leiders en adviseurs die de onderstroom willen herkennen
            en daar concreet mee willen werken in hun rol.
          </p>

          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10">
            {heroStats.map((stat) => (
              <span
                key={stat}
                className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-sm text-[#F9EBEF] font-light"
              >
                {stat}
              </span>
            ))}
          </div>

          <button onClick={() => setLeergangDialogOpen(true)} className="luxe-button">
            Meld je aan voor de leergang
          </button>
        </div>
      </section>

      {/* Info bar */}
      <section className="py-10 md:py-12 px-6 relative z-30 -mt-6">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <Badge className="bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide mb-6">
              {featuredEditie.seizoen} {featuredEditie.jaar}
            </Badge>
            <div className="grid sm:grid-cols-2 gap-8">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] shrink-0">
                  <Calendar size={24} />
                </div>
                <div className="text-left">
                  <p className="text-xs text-[#854C73] uppercase tracking-wider font-bold mb-1">Startdatum</p>
                  <p className="text-[#6b4912] text-xl font-serif">{startDatumLabel}</p>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] shrink-0">
                  <MapPin size={24} />
                </div>
                <div className="text-left">
                  <p className="text-xs text-[#854C73] uppercase tracking-wider font-bold mb-1">Locatie</p>
                  <a
                    href={featuredEditie.location.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6b4912] hover:text-[#854C73] text-xl font-serif transition-colors underline decoration-dotted"
                  >
                    {featuredEditie.location.name}, Amsterdam
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat is + editie card */}
      <section className="py-16 md:py-24 px-6 relative bg-white">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-7 space-y-8">
              <div className="space-y-6">
                <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-6">
                  Wat is een leergang <span className="italic font-light font-serif">systemisch leiderschap</span>?
                </h2>
                <div className="space-y-6 text-[#6b4912] text-lg leading-relaxed">
                  <p>
                    <strong>Systemisch leiderschap</strong> betekent: niet alleen kijken naar wat zichtbaar is
                    (gedrag, procedures, conflicten), maar ook naar de <em>onderstroom</em>, patronen,
                    loyaliteiten en dynamieken die teams en organisaties sturen.
                  </p>
                  <p>
                    In deze leergang leer je die bril <strong>praktisch</strong> toepassen: in gesprekken, in
                    casuïstiek en via systemische interventies. Geen theoretische opleiding van maanden, maar een
                    compact traject voor ervaren leiders en adviseurs.
                  </p>
                  <p>
                    Het programma heet <span className="italic font-serif text-[#854C73]">Duiken in de Onderstroom</span>
                    , een leergang systemisch leiderschap ontwikkeld door Marthe van der Molen ({SITE_NAME}).
                  </p>
                </div>
              </div>

              <div className="group rounded-3xl overflow-hidden shadow-lg max-w-md">
                <div className="aspect-[4/3] overflow-hidden">
                  <OptimizedImage
                    src={leergangSession}
                    webpSrc={leergangSession}
                    alt="Marthe van der Molen tijdens een leergang systemisch leiderschap, Duiken in de Onderstroom"
                    className="w-full h-full object-cover object-[50%_45%] transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                    width={1067}
                    height={1600}
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-5 md:sticky md:top-24 space-y-6">
              <SpotlightCard className="p-8 md:p-10">
                <Badge className="bg-[#FAEBEE] text-[#854C73] border-0 mb-4">
                  Volgende editie · {featuredEditie.seizoen} {featuredEditie.jaar}
                </Badge>
                <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-6">
                  Leergang Duiken in de Onderstroom
                </h3>
                <ul className="space-y-3 text-[#6b4912] mb-8">
                  {featuredEditie.moduleDates.map((d) => (
                    <li key={d} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                  <li className="flex items-start gap-3">
                    <MapPin size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                    <span>
                      {featuredEditie.location.name}, Amsterdam
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                    <span>Max. 8 deelnemers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                    <span>€ 1.500 excl. btw · intake &amp; eindcoaching inbegrepen</span>
                  </li>
                </ul>
                <p className="text-sm text-[#6b4912]/70 mb-6">Intake vóór start · kleine groep</p>
                <Button
                  className="w-full bg-[#854C73] hover:bg-[#590D0D] text-white"
                  onClick={() => setLeergangDialogOpen(true)}
                >
                  Aanmelden
                </Button>
              </SpotlightCard>

              <SpotlightCard className="p-8">
                <h3 className="font-serif text-2xl text-[#854C73] mb-6">Wat je leert</h3>
                <ul className="space-y-4">
                  {leerdoelen.map((goal) => (
                    <li key={goal} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span className="text-[#6b4912] leading-relaxed">{goal}</span>
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-16 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">
              Voor wie is deze leergang <span className="italic font-light font-serif">systemisch leiderschap</span>?
            </h2>
            <p className="text-[#6b4912] text-lg md:text-xl font-light max-w-2xl mx-auto">
              Leiders, managers en adviseurs die systemisch willen <em>leiden</em>, niet alleen analyseren.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {voorWieCards.map((card) => (
              <SpotlightCard key={card.title} className="p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-[#FAEBEE] text-[#854C73] rounded-full flex items-center justify-center mb-6">
                  <card.icon size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-[#854C73] mb-4">{card.title}</h3>
                <p className="text-[#6b4912] leading-relaxed font-light">{card.text}</p>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </section>

      {/* Programma */}
      <section className="py-16 md:py-24 px-6 relative bg-white">
        <GrainTexture opacity={0.1} />
        <GeoPattern />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">
              Wat leert het programma <span className="italic font-light font-serif">Duiken in de Onderstroom</span>?
            </h2>
            <p className="text-[#6b4912] text-xl font-light">
              Theorie, ervaren, oefenen en casuïstiek. Duiken in de Onderstroom.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {programmaModules.map((mod) => (
              <SpotlightCard key={mod.title} className="p-10">
                <Badge className="bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide mb-6">
                  {mod.title}
                </Badge>
                <h3 className="font-serif text-3xl text-[#854C73] mb-4">
                  {mod.subtitle === 'Duiken in de onderstroom' ? (
                    <>
                      Duiken in de <span className="italic font-light font-serif">onderstroom</span>
                    </>
                  ) : (
                    <>
                      Waarnemen en <span className="italic font-light font-serif">interveniëren</span>
                    </>
                  )}
                </h3>
                <p className="text-[#6b4912] text-lg leading-relaxed">{mod.description}</p>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </section>

      {/* Uniek */}
      <section className="py-16 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">
              Wat maakt deze leergang <span className="italic font-light font-serif">uniek</span>?
            </h2>
          </div>
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
            <div className="md:col-span-5">
              <div className="group rounded-3xl overflow-hidden shadow-lg">
                <div className="aspect-[3/4] overflow-hidden">
                  <OptimizedImage
                    src={leergangOpstelling}
                    webpSrc={leergangOpstelling}
                    alt="Deelnemers tijdens een organisatieopstelling in de leergang systemisch leiderschap"
                    className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                    width={1067}
                    height={1600}
                  />
                </div>
              </div>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-6 md:gap-8">
              {uspItems.map((item, i) => (
                <div
                  key={item.title}
                  className="text-center p-8 rounded-2xl bg-white/60 hover:bg-white transition duration-300"
                >
                  <div className="w-16 h-16 mx-auto bg-[#FAEBEE] text-[#854C73] rounded-full flex items-center justify-center mb-6">
                    <Heart size={28} strokeWidth={1.5} fill={i % 2 === 0 ? 'currentColor' : 'none'} />
                  </div>
                  <h4 className="font-serif text-2xl text-[#854C73] mb-3">{item.title}</h4>
                  <p className="text-[#6b4912] text-lg leading-relaxed font-light">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 md:py-24 px-6 relative bg-white">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <span className="text-[#854C73] font-bold tracking-widest uppercase text-xs mb-4 block">
              Ervaringen
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">
              Hoe beoordelen deelnemers de <span className="italic font-light font-serif">leergang</span>?
            </h2>
            <p className="text-[#6b4912] text-lg font-light mb-2">
              Reflecties van leiders en adviseurs die de leergang systemisch leiderschap volgden.
            </p>
            <p className="text-[#854C73] font-semibold">Gemiddeld gewaardeerd met een 9 / 10</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {leergangReviews.map((review) => (
              <div
                key={review.id}
                className="bg-[#FAEBEE] rounded-2xl p-8 border border-[#854C73]/10"
              >
                <div className="text-2xl font-serif font-bold text-[#854C73] mb-4">{review.rating}/10</div>
                <blockquote className="text-[#6b4912] leading-relaxed italic mb-6">
                  &ldquo;{review.body}&rdquo;
                </blockquote>
                <p className="text-sm text-[#854C73] font-medium">Deelnemer Leergang Onderstroom</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-10">
            <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">
              Veelgestelde <span className="italic font-light font-serif">vragen</span>
            </h2>
            <p className="text-[#6b4912] font-light">
              Over de leergang, inschrijving, data en investering.
            </p>
          </div>
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6 md:p-8">
              <Accordion type="single" collapsible className="w-full">
                {leergangFaq.map((item) => (
                  <AccordionItem key={item.id} value={item.id}>
                    <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#6b4912] pt-4 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          <Card className="mt-8 border-0 shadow-xl bg-[#854C73] text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <h3 className="font-serif text-3xl md:text-4xl mb-4">
                Start je leergang <span className="italic font-light font-serif">systemisch leiderschap</span>
              </h3>
              <p className="text-white/90 mb-8 text-lg font-light leading-relaxed max-w-2xl mx-auto">
                Ben je overtuigd? Schrijf je in voor Duiken in de Onderstroom, de leergang systemisch
                leiderschap van {SITE_NAME}.
              </p>
              <Button
                size="lg"
                variant="white"
                className="px-8 py-4 text-lg shadow-xl hover:scale-105 transition-transform"
                onClick={() => setLeergangDialogOpen(true)}
              >
                Schrijf je in
              </Button>
            </CardContent>
          </Card>

          <p className="text-sm text-[#6b4912]/60 text-center mt-8 font-light">
            Op deze leergang zijn de{' '}
            <a href={voorwaardenPdf} target="_blank" rel="noreferrer" className="underline text-[#854C73]">
              voorwaarden van Systemique (PDF)
            </a>{' '}
            van toepassing.
          </p>
        </div>
      </section>

      <LeergangOnderstroomDialog open={leergangDialogOpen} onOpenChange={setLeergangDialogOpen} />
    </>
  );
};

export default LeergangOnderstroom;
